import React, { useState, useEffect, useMemo, useCallback, useRef, lazy, Suspense } from 'react';
import { useNavigate, useLocation, useOutlet } from 'react-router-dom';
import { useAuth, getAuthState, applyDeadFamilyLoginBreakthrough, surrenderBootToSignIn } from '../components/AuthView';
import AuthView from '../components/AuthView';
import BootLoadingScreen from '../components/BootLoadingScreen';
import Sidebar from '../components/Sidebar';
import WebAudioRenderer from '../components/WebAudioRenderer';
import MiniPlayer from '../components/MiniPlayer';
import { useMaestroStore } from '../store/maestroStore';
import { useIsAnythingPlaying } from '../transport/PlaybackSnapshot';
import { useTheme, loadThemeFromPreferences } from '../lib/useTheme';
import { restoreStreamingCredentials } from '../lib/useQobuz';
import { hydrateGaplessPrefsFromServer } from '../services/gaplessPlaybackSettings';
import {
  computeOpsBannerEtaSec,
  isOpsBannerProgressStuck,
} from '../utils/opsBannerProgress';
import { loadFavorites, loadPlaylists, loadQueue } from '../lib/useUserData';
import { useUI } from '../providers/UIProvider';
import { useServices } from '../providers/ServicesProvider';
import { useSubscription } from '../providers/SubscriptionProvider';
import { CLOUD_API_URL } from '../config/serverConfig';
import { isSessionDead } from '../lib/sessionDead';
import {
  initAuthDeclawFlag,
  shouldKeepPlaybackShellDespiteAuthLoss,
} from '../lib/authDeclaw';
import {
  scheduleSilentAuthRecovery,
  shadowLogWouldBeLogout,
  awaitSilentAuthRecovery,
} from '../lib/authDeclawShadow';
import { shouldBreakthroughDeadFamilyLogin } from '../lib/authDeclawDeadFamily';
import { isBootTimeTerminalRejection } from '../lib/authTerminalLatch';
import BootWarmupStrip from '../components/BootWarmupStrip';
import SectionKeepAlive from '../components/SectionKeepAlive';
import { useBootWarmupStrip } from '../hooks/useBootWarmupStrip';
import { isLocalLibraryRoute } from '../lib/libraryViewRetention';
import { fetchCloudAuthenticated } from '../services/api';
import { libraryService } from '../services/libraryService';
import { isLibraryScanInProgress } from '../services/libraryScanCoordinator';
import {
  logLocalLibraryHealthNoticeRendered,
  resolveLocalLibraryHealthNotice,
} from '../utils/localLibraryHealthNotice';
import StripeCheckoutSlideout from '../components/StripeCheckoutSlideout';
import { navigateToSubscribe } from '../utils/billingHandoff';
import PaymentProcessingScreen from '../components/PaymentProcessingScreen';
import CheckoutReturnFailedScreen from '../components/CheckoutReturnFailedScreen';
import { SignInPrompt } from '../components/AccountMenu';
import ValetChat from '../components/ValetChat';
import SearchSlideout from '../components/SearchSlideout';
import SettingsSlideout from '../components/SettingsSlideout';
import ViewErrorBoundary from '../components/ViewErrorBoundary';
import useAppNavigation from '../hooks/useAppNavigation';
import useBatonConnection from '../hooks/useBatonConnection';
import useRendererConnectivity from '../hooks/useRendererConnectivity';
import {
  useIOSNowPlaying,
  useIOSPlaybackSync,
  useIOSBackgroundAdvance,
  useIOSRemoteCommands,
  useIOSForegroundSync,
  useNativeQueueSync,
} from '../hooks/useIOSNowPlaying';
import { useIOSRouteSwitch } from '../hooks/useIOSRouteSwitch';
import { useMediaSession } from '../hooks/useMediaSession';
import { useRemotePositionTracker } from '../hooks/useRemotePositionTracker';
import { useRemoteAdvanceTimer } from '../hooks/useRemoteAdvanceTimer';
import { useEllaRadioContinuation } from '../hooks/useEllaRadioContinuation';
import usePersonalization from '../hooks/usePersonalization';
import { useQueueSync } from '../hooks/useQueueSync';
import { TransportState } from '../transport/TransportState';
import { QueueController } from '../transport/QueueController';
import useProactiveAssist from '../hooks/useProactiveAssist';
import ProactiveAssist from '../components/ProactiveAssist';
import QobuzSubscribeModal from '../components/QobuzSubscribeModal';
import HelpSlideout from '../components/HelpSlideout';
import AboutSlideout from '../components/AboutSlideout';
import useLocationGuard from '../hooks/useLocationGuard';
import { useLocalLibraryInteractiveGate } from '../hooks/useLocalLibraryInteractive';
import EnrichmentProgress from '../components/library/EnrichmentProgress';
import LocationManager from '../components/LocationManager';
import HomeNetworkAccessModal from '../components/HomeNetworkAccessModal';
import { warmMicPermission } from '../hooks/useMicrophone';
import useAutoUsbDac from '../hooks/useAutoUsbDac';
import { useLibraryRefHydrationValidation } from '../hooks/useLibraryRefHydrationValidation';
import UpdateTransition from '../components/UpdateTransition';
import FDAPermissionPrompt from '../components/FDAPermissionPrompt';
import ProxyFetchFailedPrompt from '../components/ProxyFetchFailedPrompt';
import ProxyIsolationBanner from '../components/ProxyIsolationBanner';
import ServiceSessionDeadBanner from '../components/ServiceSessionDeadBanner';
import BatonConnectionBanner from '../components/BatonConnectionBanner';
import IpcTransportDegradedBanner from '../components/IpcTransportDegradedBanner';
import SessionExpiredBridge from '../components/SessionExpiredBridge';
import AuthHealthBridge from '../components/AuthHealthBridge';
import AuthReauthRequiredPanel from '../components/AuthReauthRequiredPanel';
import LocalCoreInitFailedPrompt from '../components/LocalCoreInitFailedPrompt';
import GuidedTour from '../components/GuidedTour';
import FullAlbumOverlay from '../components/FullAlbumOverlay';
import TrialBanner from '../components/TrialBanner';
import ConnectorUpdateBanner from '../components/ConnectorUpdateBanner';
import ConnectorMinimumVersionBanner from '../components/ConnectorMinimumVersionBanner';
import WebBundleUpdateBanner from '../components/WebBundleUpdateBanner';
import BetaBadge from '../components/BetaBadge';
import BetaWelcomeToast from '../components/BetaWelcomeToast';
import TrialExpiredScreen from '../components/TrialExpiredScreen';
// CheckoutRequiredScreen no longer used — pending_checkout users redirect to /subscribe
import { useTranslation } from '../i18n/useTranslation';
import { startAuthInitWatchdog, stopAuthInitWatchdog } from '../lib/authInitWatchdog';
import { isBridgeAvailable } from '../lib/playback-bridge';
import { rlog, startRemoteLogging, isDebugLoggingEnabled } from '../utils/remoteLogger';
import useJriverKeyboardShortcuts from '../hooks/useJriverKeyboardShortcuts';
import { isIOSNativeApp, isNativeApp, detectPlatform, isIosShell } from '../utils/platform';
import IosSubscriptionManageHint from '../components/IosSubscriptionManageHint';
import { useOperationProgressStore } from '../store/operationProgressStore';
import { useShallow } from 'zustand/react/shallow';
import { SIDEBAR_DOCK_WIDTH } from '../constants/sidebarLayout';

if (import.meta.env.DEV) {
  console.log('[Platform]', detectPlatform());
}

function AdminStats() {
  const [realUsers, setRealUsers] = useState(null);
  const [online, setOnline] = useState(null);
  useEffect(() => {
    const token = (() => { try { return JSON.parse(localStorage.getItem('maestro_auth') || '{}').accessToken; } catch { return ''; } })();
    const headers = { Authorization: `Bearer ${token}` };
    fetch(`${CLOUD_API_URL}/api/admin/panel/dashboard/stats`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) {
          const paying = d.activeSubscribers ?? 0;
          const trial = d.trialUsers ?? 0;
          setRealUsers(paying + trial);
        }
      })
      .catch(() => {});
    fetch(`${CLOUD_API_URL}/api/presence/summary`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.onlineCount != null) setOnline(d.onlineCount); })
      .catch(() => {});
  }, []);
  if (realUsers == null && online == null) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {online != null && (
        <span className="backdrop-blur-md rounded-full flex items-center gap-1.5" style={{ background: 'rgba(0,0,0,0.3)', padding: '5px 10px', fontSize: 12, fontWeight: 700, color: '#22c55e', letterSpacing: '-0.01em' }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', animation: 'adminPulse 1.5s ease-in-out infinite', flexShrink: 0 }} />
          {online} online
        </span>
      )}
      {realUsers != null && (
        <span className="backdrop-blur-md rounded-full" style={{ background: 'rgba(0,0,0,0.3)', padding: '5px 10px', fontSize: 12, fontWeight: 700, color: '#C9A84C', letterSpacing: '-0.01em' }}>
          {realUsers} users
        </span>
      )}
    </div>
  );
}


const Spinner = () => (
  <div style={{
    minHeight: '100vh',
    background: 'var(--bg-primary)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>
    <div style={{
      width: 48, height: 48,
      border: '3px solid rgba(201, 168, 76, 0.2)',
      borderTopColor: '#C9A84C', borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const PageLoadingFallback = () => (
  <div style={{ minHeight: '40vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{
      width: 40, height: 40,
      border: '3px solid rgba(201, 168, 76, 0.2)',
      borderTopColor: '#C9A84C', borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const LocalLibraryPageRetained = lazy(() => import('../pages/LocalLibraryPage'));

export default function AppLayout() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const outlet = useOutlet();
  const isLibraryRoute = isLocalLibraryRoute(location.pathname);
  const librarySectionCachedRef = useRef(isLibraryRoute);
  if (isLibraryRoute) librarySectionCachedRef.current = true;
  const librarySectionCached = librarySectionCachedRef.current;
  const bootWarmup = useBootWarmupStrip(auth);
  const currentTrack = useMaestroStore((s) => s.currentTrack);
  useLocalLibraryInteractiveGate();
  const { theme: currentTheme, setTheme } = useTheme();
  const { services, voiceSearchEnabled } = useServices();
  const { stripeSlideout, setStripeSlideout, showSubscribedWelcome, setShowSubscribedWelcome, trialExpired, setTrialExpired, openStripeCheckout, subscriptionData, checkoutReturnState, setCheckoutReturnState, fetchSubscriptionStatus } = useSubscription();
  const { t } = useTranslation();

  useEffect(() => {
    void initAuthDeclawFlag();
  }, []);

  useEffect(() => {
    startAuthInitWatchdog(getAuthState().isLoading);
    return () => stopAuthInitWatchdog();
  }, []);

  const declawPlaybackShell = !auth.isAuthenticated && shouldKeepPlaybackShellDespiteAuthLoss();
  const bootSignInRequired = isBootTimeTerminalRejection();
  const [deadFamilyLogin, setDeadFamilyLogin] = useState(false);
  /** #138 belt only — does not alter normal auth.isLoading dismiss. */
  const [bootSurfaceForcedDismiss, setBootSurfaceForcedDismiss] = useState(false);

  useEffect(() => {
    if (!declawPlaybackShell) {
      setDeadFamilyLogin(false);
      return;
    }
    void shadowLogWouldBeLogout('app_layout_auth_gate', 'AppLayout:!isAuthenticated', {
      is_loading: auth.isLoading,
    }, null, true);
    scheduleSilentAuthRecovery('app_layout_auth_gate');

    let cancelled = false;
    void (async () => {
      await awaitSilentAuthRecovery();
      if (cancelled) return;
      if (shouldBreakthroughDeadFamilyLogin('app_layout_auth_gate')) {
        applyDeadFamilyLoginBreakthrough();
        setDeadFamilyLogin(true);
      }
    })();

    return () => { cancelled = true; };
  }, [declawPlaybackShell, auth.isLoading]);

  useEffect(() => {
    if (voiceSearchEnabled) warmMicPermission();
  }, [voiceSearchEnabled]);

  // Bootstrap the local proxy port once at app init so AlbumGrid /
  // AlbumDetailView / LocalAlbumPage can build embedded-cover URLs
  // synchronously. Safe in browser preview (returns null and the
  // online-cover fallback chain takes over). Connector dynamic import
  // keeps the web-only bundle clean.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const mod = await import('../lib/connector');
        if (cancelled) return;
        if (typeof mod.ensureProxyPort === 'function') {
          await mod.ensureProxyPort();
        }
        if (typeof mod.ensureConnectorInfo === 'function') {
          await mod.ensureConnectorInfo();
        }
        const { probeLocalThumbEndpoint } = await import('../utils/tileCoverArt');
        await probeLocalThumbEndpoint();
      } catch {
        /* non-Tauri environment — silent */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const {
    toastMessage, toastSeverity, sidebarOpen, setSidebarOpen, dismissToast,
    sidebarPinned, toggleSidebarPinned,
    showSettings, setShowSettings, showAuthModal, setShowAuthModal,
    showValetChat, setShowValetChat,
    signInPrompt, setSignInPrompt,
    showLocalPicker, setShowLocalPicker,
  } = useUI();

  const closeSidebar = useCallback(() => setSidebarOpen(false), [setSidebarOpen]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--sidebar-docked-width',
      sidebarPinned ? `${SIDEBAR_DOCK_WIDTH}px` : '0px',
    );
    return () => {
      document.documentElement.style.removeProperty('--sidebar-docked-width');
    };
  }, [sidebarPinned]);

  const navAndCloseSidebar = useCallback((fn) => {
    fn?.();
    if (!sidebarPinned) setSidebarOpen(false);
  }, [sidebarPinned, setSidebarOpen]);

  const nav = useAppNavigation();
  const { isScrubbingRef } = useBatonConnection();
  useRendererConnectivity();
  useIOSNowPlaying();
  useIOSPlaybackSync();
  useIOSBackgroundAdvance();
  useIOSRemoteCommands();
  useIOSForegroundSync();
  useNativeQueueSync();
  useIOSRouteSwitch();
  useMediaSession();
  useRemotePositionTracker();
  useRemoteAdvanceTimer();
  useEllaRadioContinuation();
  usePersonalization();
  useQueueSync();
  useAutoUsbDac();
  useLibraryRefHydrationValidation();

  // Always-on zone profile registration: sync discovered zones to backend.
  // Uses imperative subscribe() instead of a selector to avoid re-rendering
  // AppLayout on every discovery update (which disrupts transport state).
  // Reads networkId from sessionStorage (set by useNetworkId hook) — fire-and-forget.
  const syncedZonesRef = useRef(new Set());
  useEffect(() => {
    if (!auth.isAuthenticated || !auth.accessToken) return;

    let timer = null;
    let lastZonesRef = null;

    const processZones = () => {
      const zones = useMaestroStore.getState().discovery?.discoveredZones;
      if (zones === lastZonesRef) return;
      lastZonesRef = zones;
      if (!zones?.length) return;
      const unseen = zones.filter(
        (z) => z.id && z.id !== 'this_computer' && z.id !== 'local' && !syncedZonesRef.current.has(z.id),
      );
      if (!unseen.length) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(async () => {
        const { getNetworkIdSync } = await import('../hooks/useNetworkId');
        const networkId = getNetworkIdSync();
        const { useZoneProfileStore } = await import('../store/zoneProfileStore');
        const store = useZoneProfileStore.getState();
        for (const z of unseen) {
          syncedZonesRef.current.add(z.id);
          store.upsertZone(z.id, z.name || z.id, networkId).catch(() => {});
          store.markSeen(z.id, networkId).catch(() => {});
        }
      }, 2000);
    };

    processZones();
    const unsub = useMaestroStore.subscribe(processZones);
    return () => { unsub(); if (timer) clearTimeout(timer); };
  }, [auth.isAuthenticated, auth.accessToken]);

  useEffect(() => {
    TransportState.init();
    QueueController.init();
    if (isDebugLoggingEnabled()) startRemoteLogging();
    return () => {
      TransportState.destroy();
      QueueController.destroy();
    };
  }, []);

  // Remote debug: log every playbackState transition
  useEffect(() => {
    if (!isDebugLoggingEnabled()) return;
    const unsub = useMaestroStore.subscribe((s, prev) => {
      if (s.playbackState !== prev?.playbackState) {
        rlog('STORE', 'playbackState', { from: prev?.playbackState, to: s.playbackState, track: s.currentTrack?.title || '' });
      }
    });
    return unsub;
  }, []);
  const locationGuard = useLocationGuard(auth.isAuthenticated);

  // On every mount — reset Connector playback state from previous session.
  // Discovery zones are NOT cleared here — ClientDiscoveryHandler overwrites
  // them with a fresh array on reconnect, and showing stale zones during the
  // reconnect window is better than flashing "This Computer" only.
  useEffect(() => {
    const store = useMaestroStore.getState();
    store.clearConnectorState();
    import('../transport/zoneRestorePick').then(({ hydrateSavedZoneOnBoot }) => {
      hydrateSavedZoneOnBoot();
    });
  }, []);

  // Presence heartbeat — fires every 5 min while tab is visible + authenticated
  // Uses getAuthState() to always read the latest token (survives JWT refresh).
  useEffect(() => {
    if (!auth.isAuthenticated || !auth.accessToken) return;
    const ping = () => {
      if (document.visibilityState === 'hidden') return;
      if (isSessionDead()) return;
      const liveToken = getAuthState()?.accessToken;
      if (!liveToken) return;
      fetch(`${CLOUD_API_URL}/api/presence/heartbeat`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${liveToken}` },
        credentials: 'omit',
      }).catch(() => {});
    };
    ping();
    const id = setInterval(ping, 300_000);
    const onVisChange = () => { if (document.visibilityState === 'visible') ping(); };
    document.addEventListener('visibilitychange', onVisChange);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVisChange); };
  }, [auth.isAuthenticated, auth.accessToken]);

  // iOS background playback: do NOT stop playback on screen lock / app background.
  // The native side (NativeQueueManager, AVAudioSession) owns background audio.
  // A previous handler here called stop() on visibilitychange→hidden, which killed
  // playback every time the user locked the screen — defeating background audio entirely.

  // Stop network renderers when the desktop app is closed (beforeunload).
  // Without this, UPnP devices like Bridge II keep playing after the app exits.
  // pagehide fires on iOS tab-close too, but beforeunload is desktop-only safe.
  useEffect(() => {
    const cleanup = () => {
      const store = useMaestroStore.getState();
      const renderer = store.activeConnectorRenderer;
      if (!renderer) return;
      try {
        if (window.__TAURI__) {
          const { invoke } = window.__TAURI__.core || window.__TAURI__;
          invoke?.('stop', { rendererId: renderer.id });
        }
      } catch { /* best-effort on exit */ }
    };
    window.addEventListener('beforeunload', cleanup);
    window.addEventListener('pagehide', cleanup);
    return () => {
      window.removeEventListener('beforeunload', cleanup);
      window.removeEventListener('pagehide', cleanup);
    };
  }, []);

  // Detect Maestro bridge — Tauri injects window.__TAURI__ via withGlobalTauri
  const desktopMode = useMaestroStore((s) => s.desktopMode);
  useEffect(() => {
    if (desktopMode === 'full') return;
    let attempts = 0;
    const poll = setInterval(() => {
      attempts++;
      if (window.__TAURI__ || window.__MAESTRO_CONNECTOR__ || window.__maestroNative) {
        useMaestroStore.getState().setDesktopMode('full');
        console.log('[AppLayout] Maestro Connector/Native detected — Full Feature Mode activated');
        clearInterval(poll);
      }
      if (attempts >= 10) clearInterval(poll);
    }, 500);
    return () => clearInterval(poll);
  }, []);

  // Probe local SQLite IPC on boot (data availability + migration banner — not UI visibility).
  useEffect(() => {
    if (typeof window === 'undefined' || !window.__TAURI__) return;
    useMaestroStore.getState().setIsHomeLibraryHost(true);
    import('../services/localLibraryIpcGate')
      .then((m) => {
        m.initHomeLibraryFileServerProbeGate();
        m.initLocalCatalogBrowseGate();
      })
      .catch(() => {});
    window.dispatchEvent(new CustomEvent('maestro:library-host-probed', { detail: { host: true } }));
    import('../services/libraryService')
      .then((m) => m.probeLocalLibraryHost())
      .then(async (probeOk) => {
        if (!probeOk) return;
        try {
          const c = await import('../lib/connector');
          await c.warmLocalNetworkAccess();
        } catch { /* best-effort */ }
        if (localStorage.getItem('maestro_remote_access') !== '0'
          && !localStorage.getItem('maestro_lan_setup_ack')) {
          setShowHomeLanModal(true);
        }
      })
      .catch(() => {});
  }, []);

  // After a library scan on the home host, prompt for Local Network before the user leaves.
  useEffect(() => {
    const onLibraryChanged = (e) => {
      if (e?.detail?.reason !== 'scan-complete') return;
      if (!useMaestroStore.getState().isHomeLibraryHost) return;
      if (localStorage.getItem('maestro_remote_access') === '0') return;
      if (localStorage.getItem('maestro_lan_setup_ack')) return;
      setShowHomeLanModal(true);
      import('../lib/connector').then((c) => c.warmLocalNetworkAccess()).catch(() => {});
    };
    window.addEventListener('maestro:library:changed', onLibraryChanged);
    return () => window.removeEventListener('maestro:library:changed', onLibraryChanged);
  }, []);

  // ProactiveAssist disabled — was triggering random popups on idle/repeat actions
  const suggestion = null;
  const dismissSuggestion = () => {};
  // Trial modal flow: null | 'initial' | 'thankyou' | 'confirm_cancel' | 'cancelling' | 'cancelled'
  const [trialModalStep, setTrialModalStep] = useState(null);
  const [cancelError, setCancelError] = useState(null);
  const [subscribeBusy, setSubscribeBusy] = useState(false);
  const [subscribeError, setSubscribeError] = useState(null);
  const [billingPaused, setBillingPaused] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [showGuidedTour, setShowGuidedTour] = useState(false);
  const [localLibraryHealthBanner, setLocalLibraryHealthBanner] = useState(null);
  const isHomeLibraryHost = useMaestroStore((s) => s.isHomeLibraryHost);
  const [showHomeLanModal, setShowHomeLanModal] = useState(false);
  const activeOperations = useOperationProgressStore(
    useShallow((s) => Object.values(s.operations).sort((a, b) => a.startedAt - b.startedAt)),
  );
  const [headerElapsedSec, setHeaderElapsedSec] = useState(0);
  const [headerRotateIdx, setHeaderRotateIdx] = useState(0);

  useEffect(() => {
    if (localStorage.getItem('maestro_start_tour') === 'true') {
      localStorage.removeItem('maestro_start_tour');
      setTimeout(() => setShowGuidedTour(true), 800);
      return;
    }
    if (!localStorage.getItem('maestro_desktop_welcomed')) {
      setTimeout(() => setShowGuidedTour(true), 1200);
    }
  }, []);

  useEffect(() => {
    if (!activeOperations.length) {
      setHeaderElapsedSec(0);
      return;
    }
    const tick = () => {
      const primary = activeOperations[0];
      if (!primary) return;
      setHeaderElapsedSec(Math.max(0, Math.floor((Date.now() - primary.startedAt) / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [activeOperations]);

  useEffect(() => {
    if (activeOperations.length <= 1) {
      setHeaderRotateIdx(0);
      return;
    }
    const id = setInterval(() => {
      setHeaderRotateIdx((n) => (n + 1) % activeOperations.length);
    }, 2500);
    return () => clearInterval(id);
  }, [activeOperations.length]);

  useEffect(() => {
    const demoHandler = (e) => {
      const { type } = e.detail || {};
      if (type === 'settings') {
        setShowSettings(true);
        setTimeout(() => setShowSettings(false), 1500);
      }
      if (type === 'search') {
        setSearchOpen(true);
        setTimeout(() => setSearchOpen(false), 4000);
      }
      if (type === 'help') {
        setHelpOpen(true);
        setTimeout(() => setHelpOpen(false), 4000);
      }
      if (type === 'about') {
        setAboutOpen(true);
        setTimeout(() => setAboutOpen(false), 2000);
      }
    };
    const tourHandler = () => setShowGuidedTour(true);
    window.addEventListener('maestro:demo-slideout', demoHandler);
    window.addEventListener('maestro:start-tour', tourHandler);
    return () => {
      window.removeEventListener('maestro:demo-slideout', demoHandler);
      window.removeEventListener('maestro:start-tour', tourHandler);
    };
  }, []);
  const searchHoverTimer = React.useRef(null);
  const searchClosedAtRef = useRef(0);
  const openSearch = useCallback(() => {
    if (searchHoverTimer.current) { clearTimeout(searchHoverTimer.current); searchHoverTimer.current = null; }
    if (Date.now() - searchClosedAtRef.current < 800) return;
    if (window.__maestroNative?.openVoiceSearch) {
      window.__maestroNative.openVoiceSearch();
      return;
    }
    setSearchOpen(true);
  }, []);

  /** Keyboard / menu Find — always text search slideout, never iOS voice modal. */
  const openTextSearch = useCallback(() => {
    if (searchHoverTimer.current) { clearTimeout(searchHoverTimer.current); searchHoverTimer.current = null; }
    if (Date.now() - searchClosedAtRef.current < 800) return;
    if (typeof window.__focusLibrarySearch === 'function' && window.__focusLibrarySearch()) {
      return;
    }
    setSearchOpen(true);
  }, []);

  useEffect(() => {
    window.__openSearch = () => setSearchOpen(true);
    return () => { delete window.__openSearch; };
  }, []);

  useJriverKeyboardShortcuts({ onFind: openTextSearch });

  useEffect(() => {
    const onFindFromMenu = () => openTextSearch();
    window.addEventListener('maestro:find-shortcut', onFindFromMenu);
    return () => window.removeEventListener('maestro:find-shortcut', onFindFromMenu);
  }, [openTextSearch]);
  const closeSearch = useCallback(() => {
    if (searchHoverTimer.current) { clearTimeout(searchHoverTimer.current); searchHoverTimer.current = null; }
    searchClosedAtRef.current = Date.now();
    setSearchOpen(false);
  }, []);
  const handleSearchHover = useCallback(() => {
    if (searchOpen) return;
    if (Date.now() - searchClosedAtRef.current < 800) return;
    searchHoverTimer.current = setTimeout(() => openSearch(), 400);
  }, [searchOpen, openSearch]);
  const handleSearchLeave = useCallback(() => {
    if (searchHoverTimer.current) { clearTimeout(searchHoverTimer.current); searchHoverTimer.current = null; }
  }, []);

  const hasActivePlayer = useIsAnythingPlaying();
  const clientDeviceName = useMaestroStore((s) => s.system.clientDeviceName);
  const connectionState = useMaestroStore((s) => s.system.connectionState);
  const playlists = useMaestroStore((s) => s.playlists);
  const playbackState = useMaestroStore((s) => s.playbackState);
  const hardwareConnected = connectionState === 'connected';

  const isConnector = useMemo(() => !!(window.__TAURI__ || window.__MAESTRO_CONNECTOR__ || window.__maestroNative), []);
  const isNativeiOS = useMemo(() => !!(window.__maestroNative?.platform === 'ios'), []);

  const [paymentRefreshBusy, setPaymentRefreshBusy] = useState(false);

  const handlePaymentStatusRefresh = useCallback(async () => {
    setPaymentRefreshBusy(true);
    try {
      const token = getAuthState()?.accessToken;
      if (token) {
        const res = await fetch(`${CLOUD_API_URL}/api/stripe/subscription-status`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'omit',
        });
        if (res.ok) {
          const data = await res.json();
          if (['active', 'trial', 'grace'].includes(data.subscription_tier)) {
            setCheckoutReturnState(null);
          }
        }
      }
      await fetchSubscriptionStatus();
    } finally {
      setPaymentRefreshBusy(false);
    }
  }, [fetchSubscriptionStatus, setCheckoutReturnState]);

  useEffect(() => {
    if (trialExpired && !trialModalStep) setTrialModalStep('initial');
  }, [trialExpired]);

  const handleSubscriptionCheckout = useCallback(async (cadence = 'monthly') => {
    setSubscribeBusy(true);
    setSubscribeError(null);
    try {
      const ok = await openStripeCheckout(cadence);
      if (!ok) {
        setSubscribeError('Unable to open checkout here. Opening subscribe…');
        navigateToSubscribe();
      }
    } catch (e) {
      console.error('[Subscribe]', e);
      setSubscribeError('Unable to reach the billing server. Opening subscribe…');
      navigateToSubscribe();
    } finally {
      setSubscribeBusy(false);
    }
  }, [openStripeCheckout]);

  useEffect(() => {
    if (!auth.isAuthenticated || !auth.accessToken) return;
    let cancelled = false;
    (async () => {
      try {
        const { billingService } = await import('../services/subscriptionService');
        const data = await billingService.getBillingStatus();
        if (cancelled || !data) return;
        if (data.status === 'expired' || data.status === 'cancelled') {
          setBillingPaused(true);
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [auth.isAuthenticated, auth.accessToken]);

  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const onboardingCheckStarted = useRef(false);

  useEffect(() => {
    localStorage.removeItem('maestro_skip_auth');
    try { localStorage.removeItem('maestro-library-cache'); } catch (e) { console.warn('[AppLayout] failed to clear stale library cache:', e); }
  }, []);

  // On hard page reload (F5 / pull-to-refresh), redirect to home.
  // Uses sessionStorage flag to distinguish a full reload from an
  // in-app SPA navigation — the flag survives SPA but clears on reload.
  const initialRedirectDone = useRef(false);
  useEffect(() => {
    if (initialRedirectDone.current) return;
    initialRedirectDone.current = true;
    const alreadyInApp = sessionStorage.getItem('maestro_app_active');
    if (alreadyInApp) return;
    sessionStorage.setItem('maestro_app_active', '1');
    if (location.pathname !== '/app' && location.pathname.startsWith('/app/')) {
      navigate('/app', { replace: true });
    }
  }, []);

  useEffect(() => {
    if (auth.isLoading || !auth.isAuthenticated || !auth.accessToken) return;
    loadThemeFromPreferences().then((t) => { if (t) setTheme(t); else setTheme('light'); });
    restoreStreamingCredentials();
    void hydrateGaplessPrefsFromServer();
    loadFavorites();
    loadPlaylists();
    loadQueue();

    if (typeof window !== 'undefined' && '__TAURI__' in window) {
      Promise.all([
        import('../components/AuthView').then(m => m.resolveRefreshTokenForDaemon()),
        import('../lib/authTokens'),
      ])
        .then(([creds, { isValidRefreshToken }]) => {
          if (creds.refreshToken && isValidRefreshToken(creds.refreshToken)) {
            console.log(`[AppLayout] seed auth.json if needed refresh_token length=${creds.refreshToken.length}`);
            return import('../lib/authJsonWriteGate').then(g =>
              g.writeAuthJsonIfNeeded(
                creds.refreshToken,
                creds.userId || '',
                creds.email || '',
                'AppLayout:boot',
              ),
            );
          }
          console.warn('[AppLayout] No valid refresh token for auth.json — sign out and sign in to fix');
        })
        .catch(e => console.error('[AppLayout] auth.json seed failed:', e));

      if (auth.accessToken) {
        import('../lib/connector')
          .then(c => c.setAuthToken(auth.accessToken))
          .catch(e => console.error('[AppLayout] setAuthToken failed:', e));
      }

      // If library was scanned but remote access was never enabled (local-core gap), fix on boot.
      if (localStorage.getItem('maestro_remote_access') !== '0') {
        (async () => {
          try {
            if (window.__TAURI__) {
              useMaestroStore.getState().setIsHomeLibraryHost(true);
            }
            const { probeLocalLibraryHost } = await import('../services/libraryService');
            const probeOk = await probeLocalLibraryHost();
            if (!window.__TAURI__ && !probeOk) return;

            const c = await import('../lib/connector');
            if (probeOk) await c.warmLocalNetworkAccess();

            const resp = await fetchCloudAuthenticated('/api/library/tunnel', { method: 'GET' });
            if (resp.ok) {
              const data = await resp.json();
              if (data?.tunnel_url) return;
            }
            const { libraryService } = await import('../services/libraryService');
            const stats = await libraryService.getStats();
            const trackCount = stats?.total_tracks ?? stats?.tracks ?? 0;
            if (trackCount <= 0) return;
            await c.enableHomeLibraryRemoteAccess({ accessToken: auth.accessToken });
            console.log('[AppLayout] Auto-enabled home library remote access after boot');
          } catch (e) {
            console.warn('[AppLayout] Boot remote-access enable failed:', e);
          }
        })();
      }
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.accessToken, setTheme]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await libraryService.getLocalHealthStatus();
        if (cancelled) return;
        if (status?.status !== 'failed' && status?.status !== 'degraded') return;

        const detail = status?.message || 'Local library health check failed — see logs.';
        let albumCount = null;
        try {
          const stats = await libraryService.getStats();
          albumCount = Number(stats?.total_albums ?? stats?.albums ?? 0);
          if (!Number.isFinite(albumCount)) albumCount = null;
        } catch {
          // stats unavailable — classify without album count
        }

        const notice = resolveLocalLibraryHealthNotice(detail, {
          scanActive: isLibraryScanInProgress(),
          albumCount,
        });
        logLocalLibraryHealthNoticeRendered(notice);
        if (cancelled) return;
        setLocalLibraryHealthBanner({
          message: t(notice.messageKey),
          severity: notice.severity,
        });
      } catch {
        // non-tauri or unavailable adapter path
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  // Poll tunnel status, write to Zustand, and register as a peer when tunnel is live
  const peerRegistered = useRef(false);
  useEffect(() => {
    if (!auth.isAuthenticated) return;
    const check = async () => {
      if (isSessionDead()) return;
      try {
        let tunnelUrl = null;
        let daemonTunnelUrl = null;
        let isHost = useMaestroStore.getState().isHomeLibraryHost;

        if (window.__TAURI__) {
          useMaestroStore.getState().setIsHomeLibraryHost(true);
          isHost = true;
        }

        // Try Tauri IPC first (GUI tunnel or daemon IPC) — home host only
        if (window.__TAURI__ && isHost) {
          try {
            const c = await import('../lib/connector');
            daemonTunnelUrl = await c.queryDaemonTunnelUrl();
            tunnelUrl = daemonTunnelUrl || (await c.getTunnelUrl());
          } catch { /* Tauri IPC failed, fall through */ }
        }

        // If Tauri didn't return a tunnel URL (daemon manages it), check Mycelium
        if (!tunnelUrl) {
          try {
            const resp = await fetchCloudAuthenticated('/api/library/tunnel', { method: 'GET' });
            if (resp.ok) {
              const data = await resp.json();
              tunnelUrl = data?.tunnel_url || null;
            }
          } catch { /* Mycelium unreachable */ }
        }

        // Home host: daemon has a live URL but Mycelium may still hold a stale trycloudflare URL.
        if (window.__TAURI__ && isHost && daemonTunnelUrl) {
          try {
            const c = await import('../lib/connector');
            const machineId = await c.getMachineId();
            const resp = await fetchCloudAuthenticated('/api/library/tunnel', { method: 'GET' });
            const registered = resp.ok ? (await resp.json())?.tunnel_url : null;
            if (registered !== daemonTunnelUrl) {
              const { libraryService } = await import('../services/libraryService');
              await libraryService.registerTunnel(daemonTunnelUrl, machineId);
              tunnelUrl = daemonTunnelUrl;
              console.log('[AppLayout] Synced live tunnel URL with cloud');
            }
          } catch (e) {
            console.warn('[AppLayout] Tunnel URL sync failed:', e);
          }
        }

        // End-to-end probe: tunnel URL registered ≠ home Connector reachable + SQLite ready.
        if (tunnelUrl || !isHost) {
          try {
            const { libraryService } = await import('../services/libraryService');
            let health = await libraryService.getTunnelHealth();
            let endToEndOk = health?.ok && (health?.machines || []).some((m) => m.reachable && m.catalog_ready !== false);

            // Home host: probe the live local file server (never a cached/hardcoded port).
            if (isHost && window.__TAURI__) {
              try {
                const { probeHomeLibraryFileServerWithBackoff } = await import('../services/localLibraryIpcGate');
                const localProbe = await probeHomeLibraryFileServerWithBackoff();
                if (localProbe.ok) {
                  endToEndOk = true;
                } else if (!localProbe.skipped && !localProbe.ok) {
                  console.warn(
                    '[AppLayout] Home library file-server probe failed:',
                    localProbe.reason ?? localProbe.error ?? localProbe.status,
                  );
                }
              } catch (probeErr) {
                console.warn('[AppLayout] Home library file-server probe error:', probeErr);
              }
            }

            if (!endToEndOk && isHost && daemonTunnelUrl) {
              health = await libraryService.getTunnelHealth();
              endToEndOk = health?.ok && (health?.machines || []).some((m) => m.reachable && m.catalog_ready !== false);
            }
            // Remote laptop: health can lag stale KV URLs — verify with a live catalog fetch.
            if (!isHost && !endToEndOk) {
              try {
                const remote = await libraryService.getAlbums({ limit: 1, offset: 0 });
                const remoteCount = remote?.albums?.length || Number(remote?.total) || 0;
                if (remoteCount > 0 || remote?.__source === 'remote_tunnel') {
                  endToEndOk = true;
                }
              } catch { /* catalog probe failed */ }
            }
            useMaestroStore.getState().setTunnelConnected(endToEndOk);
            try {
              const { setRemoteHomeLibraryAvailable } = await import('../services/localLibraryIpcGate');
              setRemoteHomeLibraryAvailable(!isHost && endToEndOk);
              window.dispatchEvent(new CustomEvent('maestro:remote-home-library-changed'));
            } catch { /* optional */ }
            if (tunnelUrl && !endToEndOk) {
              const { areAllLibrarySourcesOffline } = await import('../services/localLibraryIpcGate');
              if (!areAllLibrarySourcesOffline()) {
                console.warn('[AppLayout] Tunnel registered but home library probe failed:', health);
              }
            }
          } catch (e) {
            console.warn('[AppLayout] Tunnel health probe failed:', e);
            if (tunnelUrl) useMaestroStore.getState().setTunnelConnected(false);
          }
        } else {
          useMaestroStore.getState().setTunnelConnected(false);
          try {
            const { setRemoteHomeLibraryAvailable } = await import('../services/localLibraryIpcGate');
            setRemoteHomeLibraryAvailable(false);
            window.dispatchEvent(new CustomEvent('maestro:remote-home-library-changed'));
          } catch { /* optional */ }
        }

        // Register as peer once when tunnel comes online (home host only)
        if (isHost && tunnelUrl && !peerRegistered.current) {
          peerRegistered.current = true;
          try {
            const machineId = localStorage.getItem('maestro_machine_id');
            if (machineId) {
              const { registerPeer } = await import('../hooks/usePeerNetwork');
              const connectorInfo = window.__MAESTRO_CONNECTOR__;
              await registerPeer({
                machine_id: machineId,
                machine_name: machineId.split('-').slice(0, -1).join('-') || machineId,
                tunnel_url: tunnelUrl,
                app_version: connectorInfo?.version || undefined,
              });
            }
          } catch (e) {
            console.warn('[AppLayout] Peer registration failed:', e);
          }
        }
        if (!tunnelUrl) peerRegistered.current = false;
      } catch {
        useMaestroStore.getState().setTunnelConnected(false);
      }
    };
    check();
    const interval = setInterval(check, 30000);
    return () => clearInterval(interval);
  }, [auth.isAuthenticated, auth.accessToken]);

  useEffect(() => {
    if (!auth.isAuthenticated || !auth.accessToken) return;
    if (onboardingCheckStarted.current) return;
    onboardingCheckStarted.current = true;

    (async () => {
      if (isSessionDead()) {
        setOnboardingChecked(true);
        return;
      }
      try {
        const res = await fetch('https://api.maestroaudio.net/api/user/profile', {
          headers: { Authorization: `Bearer ${auth.accessToken}` },
        });
        if (!res.ok) throw new Error('profile fetch failed');
        const data = await res.json();
        const tier = data?.profile?.subscription_tier;
        const hadTrial = data?.profile?.had_trial;
        const hasStripe = !!data?.profile?.stripe_customer_id;
        if (tier === 'pending_checkout' || (tier === 'expired' && !hadTrial && !hasStripe)) {
          setOnboardingChecked(true);
          return;
        }
        if (data?.profile?.onboarding_completed) {
          localStorage.setItem('maestro_onboarding_complete', 'true');
        } else {
          localStorage.removeItem('maestro_onboarding_complete');
          window.location.replace('/onboarding/language');
          return;
        }
      } catch {}
      setOnboardingChecked(true);
    })();
  }, [auth.isAuthenticated, auth.accessToken]);

  if (!isNativeApp()) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#0a0a14', fontFamily: "'Inter', -apple-system, sans-serif", padding: 20,
      }}>
        <div style={{ textAlign: 'center', maxWidth: 440 }}>
          <div style={{
            fontSize: 20, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.85), #C9A84C 60%, #E8D48B)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
            marginBottom: 32,
          }}>Maestro</div>
          <h1 style={{ color: '#fff', fontSize: 22, fontWeight: 700, margin: '0 0 12px' }}>
            Desktop App Required
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 15, lineHeight: 1.6, margin: '0 0 28px' }}>
            Maestro is designed to run as a native application, not in a web browser.
            Please download and install the Maestro app for the best experience.
          </p>
          <a
            href="/download"
            style={{
              display: 'inline-block', padding: '14px 32px', background: '#C9A84C',
              color: '#1a1a2e', borderRadius: 12, fontSize: 15, fontWeight: 700,
              textDecoration: 'none',
            }}
          >
            Download Maestro
          </a>
        </div>
      </div>
    );
  }

  // Dismiss owner unchanged: auth.isLoading + terminal gates. #138 only adds
  // bootSurfaceForcedDismiss as a structural anti-wedge belt at t=30s.
  if (
    auth.isLoading
    && !deadFamilyLogin
    && !bootSignInRequired
    && !auth.authInitTimedOut
    && !auth.sessionBootstrapFailed
    && !bootSurfaceForcedDismiss
  ) {
    return (
      <BootLoadingScreen
        signInLabel={t('auth.boot.signInNow')}
        onSignInNow={() => surrenderBootToSignIn()}
        onForceDismiss={() => setBootSurfaceForcedDismiss(true)}
      />
    );
  }
  if (!auth.isAuthenticated && (deadFamilyLogin || bootSignInRequired || !declawPlaybackShell || auth.authInitTimedOut || auth.sessionBootstrapFailed)) {
    return <AuthView onAuthenticated={() => { navigate('/app', { replace: true }); }} />;
  }
  if (!onboardingChecked) return <Spinner />;

  if (checkoutReturnState === 'verifying') {
    return <Spinner />;
  }

  if (checkoutReturnState === 'failed') {
    return (
      <CheckoutReturnFailedScreen
        onRetrySubscribe={isIosShell() ? undefined : () => { setCheckoutReturnState(null); navigateToSubscribe(); }}
        onDismiss={() => setCheckoutReturnState(null)}
      />
    );
  }

  const isPendingPayment = subscriptionData?.subscription_tier === 'pending_payment'
    || checkoutReturnState === 'processing';

  if (isPendingPayment) {
    return (
      <PaymentProcessingScreen
        onRefresh={handlePaymentStatusRefresh}
        refreshing={paymentRefreshBusy}
        onRetrySubscribe={isIosShell() ? undefined : () => { navigateToSubscribe(); }}
      />
    );
  }

  // #389: lapsed entitlement blocks playback via trialExpired modal — never redirect /app
  // to /welcome-back (that killed Settings → Monthly / door 3). Billing surfaces stay
  // reachable; shouldBlockAccess() in SubscriptionProvider aligns day-0 limbo + day-1 expired.

  const activeView = (() => {
    const path = location.pathname.replace('/app', '').replace(/^\//, '');
    if (!path || path === 'home') return 'home';
    if (path.startsWith('artist/')) return 'artist_detail';
    if (path.startsWith('person/')) return 'person_detail';
    if (path.startsWith('album/')) return 'album_detail';
    if (path.startsWith('browse')) return 'grid';
    if (path.startsWith('local-album/')) return 'local_album_detail';
    if (path.startsWith('local-library')) {
      if (new URLSearchParams(location.search).get('album')) return 'local_album_detail';
      return 'local_library';
    }
    if (path.startsWith('library')) return 'grid';
    if (path.startsWith('genre')) return 'genres';
    if (path === 'radio') return 'live_radio';
    if (path === 'ella-radio' || path === 'maestro-radio') return 'ella_radio';
    if (path === 'octave-radio') return 'octave_radio';
    if (path.startsWith('qobuz')) return 'qobuz_browse';
    if (path === 'favorites') return 'favorites';
    if (path === 'audiophile-playlists') return 'audiophile_playlists';
    if (path === 'genre-playlists') return 'genre_playlists';
    if (path === 'mood-playlists') return 'mood_playlists';
    if (path === 'season-playlists') return 'season_playlists';
    if (path === 'my-playlists') return 'my_playlists';
    if (path === 'smart-playlists' || path.startsWith('smart-playlist/')) return 'smart_playlists';
    if (path === 'favorite-playlists') return 'favorites_playlists';
    if (path === 'stream-albums') return 'stream_albums';
    if (path === 'stream-artists') return 'stream_artists';
    if (path.startsWith('playlist/')) return 'playlist_detail';
    if (path.startsWith('valet/')) {
      const sub = path.replace('valet/', '');
      return `valet_${sub}`;
    }
    if (path === 'valet') return 'valet_for_you';
    if (path === 'devices') return 'devices';
    if (path === 'prism') return 'prism';
    if (path === 'history') return 'history';
    if (path === 'support') return 'support';
    if (path === 'account') return 'account';
    // Unrecognized path — no sidebar item should highlight
    return null;
  })();

  const primaryOperation = activeOperations.length
    ? activeOperations[headerRotateIdx % activeOperations.length]
    : null;

  const LIBRARY_SCAN_HEADER_PHASES = new Set([
    'enumerating', 'discovering', 'reconciling', 'scanning',
    'finalizing', 'indexing', 'walk_complete', 'artwork', 'preparing',
  ]);
  const isLibraryScanOperation = (op) => {
    if (!op) return false;
    const phase = String(op.phase ?? '').toLowerCase();
    if (LIBRARY_SCAN_HEADER_PHASES.has(phase)) return true;
    const label = String(op.label ?? '').toLowerCase();
    return label.includes('walking nas')
      || label.includes('reading tags')
      || label.includes('discovering files')
      || label.includes('building album');
  };
  /** Library grid owns scan progress via OperationHeaderProgressStrip — no title-bar overlap. */
  const headerOperation = (activeView === 'local_library' && isLibraryScanOperation(primaryOperation))
    ? null
    : primaryOperation;

  const showBufferingHeader = playbackState === 'buffering';
  const primaryProgress = headerOperation?.progress;
  const clampedHeaderProgress =
    typeof primaryProgress === 'number'
      ? Math.max(0, Math.min(1, primaryProgress))
      : undefined;
  const opsBannerStuck = isOpsBannerProgressStuck({
    headerElapsedSec,
    clampedHeaderProgress,
  });
  const etaSec = computeOpsBannerEtaSec({
    headerElapsedSec,
    clampedHeaderProgress,
  });

  /** In-library album overlay owns its own back control (see LocalLibraryPage). */
  const isLibraryRouteAlbumDetail = activeView === 'local_album_detail'
    && location.pathname.replace('/app', '').replace(/^\//, '').startsWith('local-library');

  return (
    <div className="maestro-main relative min-h-screen font-sans overflow-hidden" style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)', transition: 'background-color 0.3s ease, color 0.3s ease' }}>
      {(isConnector || !isNativeiOS) && <WebAudioRenderer />}

      {!sidebarPinned && (
        <div
          className={`fixed inset-0 z-[115] bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${sidebarOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
          onClick={closeSidebar}
        >
          <div
            className={`absolute left-0 top-0 bottom-0 transition-transform duration-300 ease-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
            style={{ width: SIDEBAR_DOCK_WIDTH }}
            onClick={(e) => e.stopPropagation()}
          >
            <Sidebar
              activeView={activeView}
              libraryTab="albums"
              deviceName={clientDeviceName}
              connected={hardwareConnected}
              services={services}
              onNavigateHome={() => navAndCloseSidebar(nav.goHome)}
              onNavigateGenres={() => navAndCloseSidebar(nav.goGenres)}
              onNavigateHistory={() => navAndCloseSidebar(nav.goHistory)}
              onNavigateDevices={() => navAndCloseSidebar(nav.goDevices)}
              onNavigatePrism={() => navAndCloseSidebar(nav.goPrism)}
              onNavigateSupport={() => navAndCloseSidebar(nav.goSupport)}
              onNavigateAccount={() => navAndCloseSidebar(() => navigate('/app/account'))}
              onNavigateLibraryTab={(tab) => navAndCloseSidebar(() => nav.goLibrary(tab))}
              onNavigateLocalLibrary={(tab) => navAndCloseSidebar(() => nav.goLocalLibrary(tab))}
              onOpenServiceAuth={(key) => {
                const k = String(key || '').trim();
                if (k === 'local') { setShowLocalPicker(true); navAndCloseSidebar(); return; }
                if (k === 'qobuz') { navAndCloseSidebar(nav.goQobuz); return; }
                if (k === 'live_radio') { navAndCloseSidebar(nav.goRadio); return; }
                if (k === 'ella_radio') { navAndCloseSidebar(nav.goEllaRadio); return; }
                if (k === 'octave_radio') { navAndCloseSidebar(nav.goOctaveRadio); return; }
                navAndCloseSidebar();
              }}
              onOpenSettings={() => navAndCloseSidebar(nav.goSettings)}
              onOpenAuth={() => setShowAuthModal(true)}
              playlists={playlists}
              onOpenPlaylist={(id) => navAndCloseSidebar(() => nav.openPlaylist(id))}
              valetEnabled={true}
              onNavigateValet={(v) => navAndCloseSidebar(() => nav.goValet(v))}
              onNavigateCommunity={() => navAndCloseSidebar(() => navigate('/app/community'))}
              onNavigateFavorites={() => navAndCloseSidebar(nav.goFavorites)}
              onNavigateAudiophilePlaylists={() => navAndCloseSidebar(nav.goAudiophilePlaylists)}
              onNavigateGenrePlaylists={() => navAndCloseSidebar(nav.goGenrePlaylists)}
              onNavigateMoodPlaylists={() => navAndCloseSidebar(nav.goMoodPlaylists)}
              onNavigateSeasonPlaylists={() => navAndCloseSidebar(nav.goSeasonPlaylists)}
              onNavigateMyPlaylists={() => navAndCloseSidebar(nav.goMyPlaylists)}
              onNavigateSmartPlaylists={() => navAndCloseSidebar(nav.goSmartPlaylists)}
              onNavigateFavoritePlaylists={() => navAndCloseSidebar(nav.goFavoritePlaylists)}
              onNavigateStreamAlbums={() => navAndCloseSidebar(nav.goStreamAlbums)}
              onNavigateStreamArtists={() => navAndCloseSidebar(nav.goStreamArtists)}
              onNavigateAudiophilePlaylist={(pid) => navAndCloseSidebar(() => nav.goAudiophilePlaylist(pid))}
              onNavigateFavoritePlaylist={(pid) => navAndCloseSidebar(() => nav.goFavoritePlaylist(pid))}
              onSearchPlaylistSelect={(pid) => navAndCloseSidebar(() => nav.navigate(`/app/playlist/${encodeURIComponent(pid)}`))}
              sidebarPinned={sidebarPinned}
              onToggleSidebarPinned={toggleSidebarPinned}
              onCloseSidebar={closeSidebar}
              docked={false}
            />
          </div>
        </div>
      )}

      <div className="relative z-10 h-screen flex">
        {sidebarPinned && (
          <div
            className="flex-shrink-0 h-screen overflow-hidden"
            style={{ width: SIDEBAR_DOCK_WIDTH, borderRight: '1px solid var(--sidebar-border)' }}
          >
            <Sidebar
              activeView={activeView}
              libraryTab="albums"
              deviceName={clientDeviceName}
              connected={hardwareConnected}
              services={services}
              onNavigateHome={() => nav.goHome()}
              onNavigateGenres={() => nav.goGenres()}
              onNavigateHistory={() => nav.goHistory()}
              onNavigateDevices={() => nav.goDevices()}
              onNavigatePrism={() => nav.goPrism()}
              onNavigateSupport={() => nav.goSupport()}
              onNavigateAccount={() => navigate('/app/account')}
              onNavigateLibraryTab={(tab) => nav.goLibrary(tab)}
              onNavigateLocalLibrary={(tab) => nav.goLocalLibrary(tab)}
              onOpenServiceAuth={(key) => {
                const k = String(key || '').trim();
                if (k === 'local') { setShowLocalPicker(true); return; }
                if (k === 'qobuz') { nav.goQobuz(); return; }
                if (k === 'live_radio') { nav.goRadio(); return; }
                if (k === 'ella_radio') { nav.goEllaRadio(); return; }
                if (k === 'octave_radio') { nav.goOctaveRadio(); return; }
              }}
              onOpenSettings={() => nav.goSettings()}
              onOpenAuth={() => setShowAuthModal(true)}
              playlists={playlists}
              onOpenPlaylist={(id) => nav.openPlaylist(id)}
              valetEnabled={true}
              onNavigateValet={(v) => nav.goValet(v)}
              onNavigateCommunity={() => navigate('/app/community')}
              onNavigateFavorites={() => nav.goFavorites()}
              onNavigateAudiophilePlaylists={() => nav.goAudiophilePlaylists()}
              onNavigateGenrePlaylists={() => nav.goGenrePlaylists()}
              onNavigateMoodPlaylists={() => nav.goMoodPlaylists()}
              onNavigateSeasonPlaylists={() => nav.goSeasonPlaylists()}
              onNavigateMyPlaylists={() => nav.goMyPlaylists()}
              onNavigateSmartPlaylists={() => nav.goSmartPlaylists()}
              onNavigateFavoritePlaylists={() => nav.goFavoritePlaylists()}
              onNavigateStreamAlbums={() => nav.goStreamAlbums()}
              onNavigateStreamArtists={() => nav.goStreamArtists()}
              onNavigateAudiophilePlaylist={(pid) => nav.goAudiophilePlaylist(pid)}
              onNavigateFavoritePlaylist={(pid) => nav.goFavoritePlaylist(pid)}
              onSearchPlaylistSelect={(pid) => nav.navigate(`/app/playlist/${encodeURIComponent(pid)}`)}
              sidebarPinned={sidebarPinned}
              onToggleSidebarPinned={toggleSidebarPinned}
              onCloseSidebar={null}
              docked
            />
          </div>
        )}
        <div id="maestro-scroll-container" className={`relative flex-1 h-screen overflow-y-auto overflow-x-hidden no-scrollbar ${hasActivePlayer ? 'has-miniplayer' : ''}`}>
          <WebBundleUpdateBanner />
          <ConnectorMinimumVersionBanner />
          <ConnectorUpdateBanner />
          {localLibraryHealthBanner && (
            <div
              data-testid="local-library-health-banner"
              data-severity={localLibraryHealthBanner.severity}
              style={{
                margin: '8px 16px 0',
                padding: '10px 14px',
                borderRadius: 10,
                background: localLibraryHealthBanner.severity === 'info'
                  ? 'rgba(59,130,246,0.12)'
                  : 'rgba(239,68,68,0.12)',
                border: localLibraryHealthBanner.severity === 'info'
                  ? '1px solid rgba(59,130,246,0.35)'
                  : '1px solid rgba(239,68,68,0.35)',
                color: localLibraryHealthBanner.severity === 'info' ? '#bfdbfe' : '#fecaca',
                fontSize: 13,
                fontWeight: localLibraryHealthBanner.severity === 'info' ? 500 : 600,
              }}
            >
              {localLibraryHealthBanner.message}
            </div>
          )}
          <TrialBanner />
          {!isIosShell() && (
          <StripeCheckoutSlideout
            open={stripeSlideout.open}
            onClose={() => setStripeSlideout({ open: false, clientSecret: null })}
            clientSecret={stripeSlideout.clientSecret}
            isResubscribe={!!subscriptionData?.had_trial}
          />
          )}

          <div className="maestro-header sticky top-0 z-50 px-3 py-3 sm:px-6 sm:py-6 flex flex-col" style={{ paddingTop: 'max(12px, env(safe-area-inset-top, 12px))', background: currentTheme === 'light' ? 'linear-gradient(to bottom, var(--bg-secondary), transparent)' : 'linear-gradient(to bottom, rgba(0,0,0,0.4), transparent)' }}>
            {(headerOperation || showBufferingHeader) && (
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: 0,
                  pointerEvents: 'none',
                  zIndex: 2,
                }}
              >
                <div
                  style={{
                    height: 2,
                    width: `${Math.round(((clampedHeaderProgress ?? 0.12) * 100))}%`,
                    background: 'linear-gradient(90deg, var(--accent-primary), #e3c977)',
                    boxShadow: '0 0 8px rgba(201,168,76,0.45)',
                    transition: 'width 220ms ease',
                  }}
                />
                <div
                  style={{
                    marginTop: 2,
                    padding: '2px 12px 0',
                    fontSize: 11,
                    color: 'var(--text-secondary)',
                    textShadow: '0 1px 2px rgba(0,0,0,0.35)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {showBufferingHeader && !headerOperation
                    ? 'Buffering audio...'
                    : (
                      <>
                        {activeOperations.length > 1 ? `${activeOperations.length} ops - ` : ''}
                        {headerOperation?.label} - {headerElapsedSec}s
                        {opsBannerStuck
                          ? ' — a command is stuck — reset'
                          : etaSec != null
                            ? ` - ETA ${etaSec}s`
                            : ''}
                      </>
                    )}
                </div>
              </div>
            )}
            <div className="flex items-center gap-1.5 sm:gap-3">
              {!sidebarPinned && (
                <button type="button" onClick={() => setSidebarOpen(true)} className="bg-black/30 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-black/50 transition border border-white/10 active:scale-95" style={{ minWidth: 36, width: 36, height: 36 }} title={t('common.menu')}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 6h16" /><path d="M4 12h16" /><path d="M4 18h16" />
                  </svg>
                </button>
              )}
              {(activeView === 'album_detail' || activeView === 'artist_detail' || activeView === 'person_detail' || activeView === 'genre_detail' || (activeView === 'local_album_detail' && !isLibraryRouteAlbumDetail)) && (
                <button onClick={() => nav.goBack()} className="bg-black/30 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-black/50 transition" style={{ minWidth: 36, height: 36 }} title={t('common.back')}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
                </button>
              )}
              <button onClick={nav.goHome} className="bg-black/30 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-black/50 transition" style={{ minWidth: 36, height: 36 }} title={t('common.home')}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
              </button>
              <div className="flex-1" />
              <a
                href="/app/support"
                className="inline-flex items-center text-xs sm:text-sm font-semibold text-white/90 hover:text-white transition shrink-0 px-1 sm:px-2"
                style={{ textDecoration: 'none' }}
                title="Support"
              >
                Support
              </a>
              {isBridgeAvailable() && (
                <span className="hidden sm:flex backdrop-blur-md rounded-full items-center gap-1.5 text-white/80" style={{ background: 'rgba(0,0,0,0.3)', padding: '4px 8px', fontSize: 9, fontWeight: 600, letterSpacing: '0.05em' }}>
                  <span style={{ color: '#22c55e', fontSize: 7 }}>●</span>
                  Connector
                </span>
              )}
              {auth.adminRole && (
                <>
                  <style>{`@keyframes adminPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }`}</style>
                  <span className="hidden sm:contents"><AdminStats /></span>
                  <button onClick={() => navigate('/app/admin')} data-guide-id="admin-icon" className="hidden sm:flex backdrop-blur-md rounded-full items-center justify-center hover:bg-black/50 transition" style={{ minWidth: 36, height: 36, background: 'rgba(0,0,0,0.3)' }} title="Admin Panel">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                  </button>
                </>
              )}
              <button onClick={() => setAboutOpen(true)} data-guide-id="about-button" className="bg-black/30 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-black/50 transition" style={{ minWidth: 36, height: 36 }} title={t('common.about')}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
              </button>
              <button onClick={() => setHelpOpen(true)} data-guide-id="help-button" className="bg-black/30 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-black/50 transition" style={{ minWidth: 36, height: 36 }} title={t('common.help')}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              </button>
              <button onClick={openSearch} onMouseEnter={handleSearchHover} onMouseLeave={handleSearchLeave} data-guide-id="search-button" className="bg-black/30 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-black/50 transition" style={{ minWidth: 36, height: 36 }} title={t('common.search')}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
              </button>
            </div>
          </div>

          <EnrichmentProgress />
          <BootWarmupStrip
            visible={bootWarmup.stripVisible && auth.isAuthenticated && !bootSignInRequired}
            message={bootWarmup.message}
            phase={bootWarmup.phase}
          />
          {librarySectionCached && (
            <SectionKeepAlive name="local-library" active={isLibraryRoute} enabled={librarySectionCached}>
              <Suspense fallback={<PageLoadingFallback />}>
                <ViewErrorBoundary viewName="local-library">
                  <LocalLibraryPageRetained />
                </ViewErrorBoundary>
              </Suspense>
            </SectionKeepAlive>
          )}
          {!isLibraryRoute ? outlet : null}
          <div style={{ padding: '12px 24px 4px', textAlign: 'center' }}>
            <button
              onClick={() => navigate('/app/support')}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-secondary, rgba(255,255,255,0.4))',
                fontSize: 12, fontWeight: 500, padding: '4px 8px',
                transition: 'color 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#C9A84C'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-secondary, rgba(255,255,255,0.4))'; }}
            >
              Need help? Contact Support
            </button>
          </div>
        </div>
      </div>

      {signInPrompt.isOpen && (
        <SignInPrompt
          feature={signInPrompt.feature}
          onClose={() => setSignInPrompt({ isOpen: false, feature: null })}
          onSignIn={() => { setSignInPrompt({ isOpen: false, feature: null }); setShowAuthModal(true); }}
        />
      )}

      {showValetChat && <ValetChat onClose={() => setShowValetChat(false)} />}

      <ProactiveAssist suggestion={suggestion} onDismiss={dismissSuggestion} />
      <QobuzSubscribeModal />
      <BetaBadge />
      <BetaWelcomeToast />
      <ViewErrorBoundary viewName="now-playing">
        <MiniPlayer isScrubbingRef={isScrubbingRef} />
      </ViewErrorBoundary>

      {!!toastMessage && (
        <div
          role="status"
          onClick={() => dismissToast()}
          style={{
          position: 'fixed',
          left: '50%',
          transform: 'translateX(-50%)',
          bottom: hasActivePlayer ? 100 : 28,
          zIndex: trialExpired ? 100001 : 99990,
          maxWidth: 'min(560px, calc(100vw - 32px))',
          padding: '12px 22px',
          borderRadius: 14,
          background: toastSeverity === 'info' ? 'rgba(18, 28, 42, 0.94)' : 'rgba(30, 24, 10, 0.94)',
          border: toastSeverity === 'info'
            ? '1px solid rgba(120, 160, 200, 0.35)'
            : '1px solid rgba(212, 175, 55, 0.5)',
          color: toastSeverity === 'info' ? 'var(--text-secondary, #c8d4e0)' : '#f5e6b8',
          fontSize: 13,
          fontWeight: 500,
          lineHeight: 1.45,
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          boxShadow: toastSeverity === 'info'
            ? '0 8px 32px rgba(0,0,0,0.35)'
            : '0 8px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(212,175,55,0.15)',
          pointerEvents: 'auto',
          cursor: 'pointer',
          animation: 'toastSlideUp 0.3s ease-out',
        }}>
          {toastSeverity === 'warning' && (
            <span style={{ color: '#d4af37', marginRight: 8 }}>⚠</span>
          )}
          {toastMessage}
        </div>
      )}

      <SearchSlideout open={searchOpen} onClose={closeSearch} />
      <ViewErrorBoundary viewName="settings">
        <SettingsSlideout open={showSettings} onClose={() => setShowSettings(false)} locationGuard={locationGuard} />
      </ViewErrorBoundary>
      <HelpSlideout open={helpOpen} onClose={() => setHelpOpen(false)} />
      <AboutSlideout open={aboutOpen} onClose={() => setAboutOpen(false)} />
      <LocationManager
        isBlocked={locationGuard.isBlocked}
        locations={locationGuard.locations}
        currentLocation={locationGuard.currentLocation}
        limit={locationGuard.limit}
        message={locationGuard.blockMessage}
        onRelease={locationGuard.releaseLocation}
        onRename={locationGuard.renameLocation}
        onMarkHome={locationGuard.markHome}
      />

      <UpdateTransition />
      <FDAPermissionPrompt />
      <ProxyFetchFailedPrompt />
      <ProxyIsolationBanner />
      <ServiceSessionDeadBanner />
      <BatonConnectionBanner />
      <IpcTransportDegradedBanner />
      <SessionExpiredBridge />
      <AuthHealthBridge />
      <AuthReauthRequiredPanel />
      <LocalCoreInitFailedPrompt />
      <FullAlbumOverlay />

      {showGuidedTour && <GuidedTour onClose={() => { setShowGuidedTour(false); localStorage.setItem('maestro_desktop_welcomed', Date.now().toString()); }} userName={auth.user?.user_metadata?.display_name || auth.user?.user_metadata?.full_name || auth.user?.user_metadata?.name || auth.user?.name || auth.user?.full_name || ''} />}

      {/* Welcome back after subscribing */}
      {showSubscribedWelcome && (() => {
        const firstName = (
          auth.user?.user_metadata?.display_name
          || auth.user?.user_metadata?.full_name
          || auth.user?.user_metadata?.name
          || auth.user?.name
          || auth.user?.email?.split('@')[0]
          || 'friend'
        ).split(' ')[0];
        return (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 99998,
          background: 'rgba(0,0,0,0.7)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 24,
        }}>
          <div style={{
            maxWidth: 420, width: '100%',
            background: 'var(--bg-primary, #121212)',
            border: '1px solid rgba(201,168,76,0.2)',
            borderRadius: 20, padding: 32,
            textAlign: 'center',
            boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: 'rgba(201,168,76,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px',
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary, #fff)', marginBottom: 10 }}>
              Welcome back{firstName ? `, ${firstName}` : ''}!
            </h2>
            <p style={{ fontSize: 15, color: 'var(--text-secondary, rgba(255,255,255,0.55))', lineHeight: 1.6, marginBottom: 24 }}>
              Your library and playlists are right where you left them. Let&apos;s pick up the music.
            </p>
            <button
              onClick={() => setShowSubscribedWelcome(false)}
              style={{
                width: '100%', fontSize: 15, fontWeight: 700, padding: '14px 0', borderRadius: 12,
                background: '#C9A84C', color: '#1a1a2e', cursor: 'pointer',
                transition: 'opacity 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
            >
              Let's Go
            </button>
          </div>
        </div>
        );
      })()}

      {billingPaused && !trialExpired && !stripeSlideout.open && <TrialExpiredScreen />}

      {/* Subscription blocking modal — hide when Stripe checkout is open */}
      {trialExpired && !stripeSlideout.open && !showSettings && (() => {
        const tier = subscriptionData?.subscription_tier;
        const hasPayment = subscriptionData?.has_payment_method;
        const isGrace = tier === 'grace';
        const isReturning = hasPayment && !isGrace;
        const isTrialEnded = !isGrace && !isReturning;

        const step = trialModalStep || 'initial';

        // Shared modal wrapper
        const modalOverlay = { position: 'fixed', inset: 0, zIndex: 99999, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 };
        const modalCard = { maxWidth: 440, width: '100%', background: 'var(--bg-primary, #121212)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, padding: 32, textAlign: 'center', boxShadow: '0 24px 80px rgba(0,0,0,0.6)' };
        const pillBtn = (bg, color) => ({ width: '100%', fontSize: 15, fontWeight: 700, padding: '14px 0', borderRadius: 50, background: bg, color, cursor: 'pointer', transition: 'opacity 0.15s', border: 'none', marginBottom: 12 });
        const hoverIn = (e) => { e.currentTarget.style.opacity = '0.85'; };
        const hoverOut = (e) => { e.currentTarget.style.opacity = '1'; };
        const iconCircle = (bg) => ({ width: 56, height: 56, borderRadius: '50%', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' });

        // "Thank you" step — auto-dismiss after 2s
        if (isTrialEnded && step === 'thankyou') {
          return (
            <div style={modalOverlay}>
              <div style={modalCard}>
                <div style={iconCircle('rgba(34,197,94,0.12)')}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                </div>
                <h2 style={{ fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 10 }}>Thank you!</h2>
                <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.55)', lineHeight: 1.6 }}>Welcome to Maestro. Back to the music...</p>
              </div>
            </div>
          );
        }

        // "Are you sure?" confirmation step
        if (isTrialEnded && step === 'confirm_cancel') {
          return (
            <div style={modalOverlay}>
              <div style={modalCard}>
                <div style={iconCircle('rgba(239,68,68,0.12)')}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
                </div>
                <h2 style={{ fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 10 }}>Are you sure?</h2>
                <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.55)', lineHeight: 1.6, marginBottom: 28 }}>
                  If you cancel, your subscription will stay active until the end of your current billing period. You can resume anytime before then from Settings.
                </p>
                <button onClick={() => setTrialModalStep('initial')} style={pillBtn('#22c55e', '#fff')} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                  No, keep my subscription!
                </button>
                <button onClick={async () => {
                  setTrialModalStep('cancelling');
                  setCancelError(null);
                  try {
                    const stored = localStorage.getItem('maestro_auth');
                    const token = stored ? JSON.parse(stored).accessToken : null;
                    const resp = await fetch(`${CLOUD_API_URL}/api/stripe/cancel-now`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    });
                    if (!resp.ok) {
                      const body = await resp.json().catch(() => ({}));
                      throw new Error(body.error || 'Cancel failed');
                    }
                    setTrialModalStep('cancelled');
                  } catch (err) {
                    console.error('[Cancel] Error:', err);
                    setCancelError(err.message);
                    setTrialModalStep('confirm_cancel');
                  }
                }} style={pillBtn('#ef4444', '#fff')} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
                  Yes, cancel my subscription
                </button>
              </div>
            </div>
          );
        }

        // "Cancelling..." spinner step
        if (isTrialEnded && step === 'cancelling') {
          return (
            <div style={modalOverlay}>
              <div style={modalCard}>
                <div style={{ width: 40, height: 40, border: '3px solid rgba(201,168,76,0.2)', borderTopColor: '#C9A84C', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 20px' }} />
                <h2 style={{ fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 10 }}>Cancelling...</h2>
                <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.55)', lineHeight: 1.6 }}>Hang tight while we process your cancellation.</p>
              </div>
            </div>
          );
        }

        // "All handled" final step — sign out after 3s
        if (isTrialEnded && step === 'cancelled') {
          setTimeout(() => { try { localStorage.clear(); } catch {} window.location.href = '/'; }, 3000);
          return (
            <div style={modalOverlay}>
              <div style={modalCard}>
                <div style={iconCircle('rgba(201,168,76,0.12)')}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                </div>
                <h2 style={{ fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 10 }}>All handled.</h2>
                <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.55)', lineHeight: 1.6 }}>
                  Your subscription has been cancelled and you won't be charged. Thank you for trying Maestro — you're always welcome back.
                </p>
              </div>
            </div>
          );
        }

        // ─── Initial modal for all three cases ─────────────────────────

        const iconColor = isGrace ? '#f59e0b' : '#C9A84C';
        const iconBg = isGrace ? 'rgba(245,158,11,0.12)' : 'rgba(201,168,76,0.12)';

        let title, message, primaryLabel, primaryAction, secondaryLabel, secondaryAction;

        if (isGrace) {
          title = 'Oops!';
          message = 'Looks like something went wrong with your payment method. Let\'s fix it and get back to the music!';
          primaryLabel = 'Update Payment Method';
          primaryAction = () => handleSubscriptionCheckout('monthly');
          secondaryLabel = 'Sign Out';
          secondaryAction = () => { try { localStorage.clear(); } catch {} window.location.href = '/'; };
        } else if (isReturning) {
          title = 'Welcome back!';
          message = 'We\'d love to have you back. Resubscribe to Maestro and pick up right where you left off with your personal music valet.';
          primaryLabel = 'Resubscribe';
          primaryAction = () => handleSubscriptionCheckout('monthly');
          secondaryLabel = 'Sign Out';
          secondaryAction = () => { try { localStorage.clear(); } catch {} window.location.href = '/'; };
        } else {
          title = 'Your subscription has ended';
          message = 'Subscribe to Maestro to keep listening. Your playlists, favorites, and library are still saved — pick up right where you left off.';
          primaryLabel = 'Subscribe to Maestro';
          primaryAction = () => handleSubscriptionCheckout('monthly');
          secondaryLabel = 'Sign Out';
          secondaryAction = () => { try { localStorage.clear(); } catch {} window.location.href = '/'; };
        }

        return (
          <div style={modalOverlay}>
            <div style={modalCard}>
              <div style={iconCircle(iconBg)}>
                {isGrace ? (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                ) : isTrialEnded ? (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                ) : (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                  </svg>
                )}
              </div>

              <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary, #fff)', marginBottom: 10 }}>
                {title}
              </h2>

              <p style={{ fontSize: 15, color: 'var(--text-secondary, rgba(255,255,255,0.55))', lineHeight: 1.6, marginBottom: 28, textAlign: isTrialEnded ? 'left' : 'center' }}>
                {message}
              </p>

              {isIosShell() ? (
                <>
                  <IosSubscriptionManageHint centered style={{ marginBottom: 20 }} />
                  <button
                    onClick={secondaryAction}
                    style={{
                      ...pillBtn('rgba(255,255,255,0.08)', '#fff'),
                      border: '1px solid rgba(255,255,255,0.15)',
                    }}
                    onMouseEnter={hoverIn} onMouseLeave={hoverOut}
                  >
                    {secondaryLabel}
                  </button>
                </>
              ) : (
              <>
              <button
                onClick={primaryAction}
                disabled={subscribeBusy}
                style={{
                  ...pillBtn('#C9A84C', '#1a1a2e'),
                  opacity: subscribeBusy ? 0.7 : 1,
                  cursor: subscribeBusy ? 'wait' : 'pointer',
                }}
                onMouseEnter={subscribeBusy ? undefined : hoverIn}
                onMouseLeave={subscribeBusy ? undefined : hoverOut}
              >
                {subscribeBusy ? 'Opening checkout…' : primaryLabel}
              </button>

              <button
                onClick={secondaryAction}
                style={{
                  ...pillBtn('rgba(255,255,255,0.08)', '#fff'),
                  border: '1px solid rgba(255,255,255,0.15)',
                }}
                onMouseEnter={hoverIn} onMouseLeave={hoverOut}
              >
                {secondaryLabel}
              </button>
              </>
              )}

              {cancelError && (
                <p style={{ color: '#ef4444', fontSize: 13, marginTop: 8 }}>Error: {cancelError}. Please try again.</p>
              )}
              {subscribeError && (
                <p style={{ color: '#ef4444', fontSize: 13, marginTop: 8 }}>{subscribeError}</p>
              )}
            </div>
          </div>
        );
      })()}

      <HomeNetworkAccessModal
        open={showHomeLanModal && isHomeLibraryHost && !!window.__TAURI__}
        onDismiss={() => setShowHomeLanModal(false)}
      />

    </div>
  );
}
